import { Fragment, useEffect, useMemo, useState } from 'react';
import { generatePath, useNavigate, useParams } from 'react-router-dom';
import Breadcrumbs from 'shell/components/shared/breadcrumb';
import { ActEmergencyEditCrumbs } from 'civil-status/constants/breadcrumb';
import StepForm from 'civil-status/components/step/form';
import { Grid, useEventCallback } from '@mui/material';
import { Form, Formik } from 'formik';
import * as yup from 'yup';
import { ObjectShape } from 'yup/lib/object';
import { __ACT_API_URL__ } from 'shell/constants/environment';
import { HttpResponse } from 'shell/models/http-response';
import { ActWizard } from 'shell/models/act-wizard';
import {
  DataWizardAct,
  IdType,
  LastStepVM,
  ParentVM,
  DocumentVM,
  QuestionarioEvento,
  RecapVM,
  RequestsType,
  StepVM,
  SoggettoList,
  ResponseValidateEvent,
  ErrorsWarnings,
  ResponsePreview,
  StatoCivile,
  Evento,
  SignatureActVM,
  AllegatoAtti,
  SectionVM,
} from 'civil-status/view-models/step-form';
import { transformerActWizard } from 'civil-status/utils/transformer-act-wizard';
import { useHttpFetch } from 'shell/hooks/use-http-fetch';
import { ResponsePromise } from 'shell/hooks/use-http-fetch/stateless/type';
import { useHttpClient } from 'shell/hooks/use-http-fetch/stateless';
import { toast } from 'react-toastify';
import { transformerActWizardToSave } from 'civil-status/utils/transformer-act-wizard-to-save';
import { AxiosResponse } from 'axios';
import { SubjectsWizardUseCase } from 'shell/models/subjects-wizard';
import { initialValuesParent } from 'civil-status/constants/initial-values-step';
import {
  InputCreate,
  createSchemaSubjectSectionStep,
} from 'civil-status/constants/schema-subject-section-step';
import { transformerActWizardDataSaved } from 'civil-status/utils/transformer-act-wizard-data-saved';
import { ItemVM } from 'shell/view-models/item';
import SearchMessages, { MessageType } from 'shell/components/shared/search-messages';
import { ModalViewerPDF } from 'shell/components/shared/pdf-modal';
import ModalPrintSuccess from 'practice/components/detail/form/print-comunication/modal';
import { DateTime } from 'luxon';
import { CodeSubject } from 'civil-status/constants/code-subject-section';
import { ID_GENDERS } from 'shell/components/shared/gender';
import { noop } from 'shell/utils/noop';
import { TypeFile } from 'practice/components/detail/form/accordion-attachments/form';
import { STATES_ACT } from 'civil-status/constants/states-act';
import { ConfigurationType } from 'civil-status/constants/configuration-type';
import { blobToBase64 } from 'shell/utils/base64-to-blob';
import { StateActProps } from 'civil-status/view-models/state-act';
import DeleteAlertModal from 'system/components/shared/delete-alert';
import TitleStep from 'civil-status/components/shared/title-step';

export type ErrorWarningToVisualizeType = {
  visibilityError?: boolean;
  titleError?: string;
  messagesError?: Array<ItemVM>;
  visibilityWarning?: boolean;
  titleWarning?: string;
  messagesWarning?: Array<ItemVM>;
};

const StepEmergencyPageComponent = (): React.ReactElement => {
  // stato riepilogo
  const [recap, setRecap] = useState<Array<RecapVM>>([]);
  // stato dell'atto
  const [stateAct, setStateAct] = useState<StateActProps>({
    isLastStep: false,
    isSignatureUSC: false,
    isTranscribeOath: false,
    isLastStepEmergency: false,
    allegatoList: [],
    idAnsc: undefined,
    idEvento: undefined,
  });

  const [lastStepInitialData, setLastStepInitialData] = useState<LastStepVM>({});

  // stato per errori e warning del bottone VALIDA EVENTO ed ANTEPRIMA
  const [errorWarningToVisualize, setErrorWarningToVisualize] =
    useState<ErrorWarningToVisualizeType>({
      visibilityError: false,
      titleError: '',
      messagesError: [],
      visibilityWarning: false,
      titleWarning: '',
      messagesWarning: [],
    });
  const [deleteAlertModal, setDeleteAlertModal] = useState<{
    open: boolean;
    step?: string;
    index?: number;
    message?: string;
    errors?: Array<ErrorsWarnings>;
    forcingCode?: string;
  }>({ open: false });
  // stato per stampa anteprima
  const [pdfModal, setPdfModal] = useState({ data: '', open: false, name: '' });
  // ModalPrintSuccess
  const [modalPrintSuccess, setModalPrintSuccess] = useState<{ open: boolean; total?: number }>({
    open: false,
  });
  // tipo evento: 1 = NASCITA, 2 = MORTE, 3 = MATRIMONIO, 4 = UNIONE CIVILE, 5 = CITTADINANZA
  const [tipoEvento, setTipoEvento] = useState<string>('');
  // usecase che effettua la chiamata per i soggetti
  const [UcCallSoggetti, setUcCallSoggetti] = useState<string>('');

  const navigate = useNavigate();

  const params = useParams();

  useEffect(() => {
    if (params.idEvento) {
      setStateAct((oldState) => ({ ...oldState, idEvento: Number.parseInt(params.idEvento, 10) }));
    }
  }, [params.idEvento]);

  const [isLoadingDraft, setIsLoadingDraft] = useState(false);

  const getDataSaved = useHttpClient(`${__ACT_API_URL__}/bozza/ricerca/${stateAct.idEvento}`, {
    options: {
      method: 'GET',
      loading: true,
      errorCatching: true,
    },
  });

  const onChangeState = useHttpClient('', {
    options: {
      method: 'PUT',
      loading: true,
      errorCatching: true,
    },
  });

  const onValid = useHttpClient('', {
    options: {
      method: 'GET',
      loading: true,
      errorCatching: true,
    },
  });

  const onPreview = useHttpClient(`${__ACT_API_URL__}/evento/anteprima/${stateAct.idEvento}`, {
    options: {
      method: 'GET',
      loading: true,
      errorCatching: true,
    },
  });

  const onPrint = useHttpClient(
    `${__ACT_API_URL__}/evento/stampa-registro-emergenza/${stateAct.idEvento}`,
    {
      options: {
        method: 'POST',
        loading: true,
        errorCatching: true,
      },
    }
  );

  const [requests, getRequests] = useHttpFetch<HttpResponse<ActWizard>, RequestsType>(
    `${__ACT_API_URL__}/wizard/questionario`,
    {
      onBootstrap: false,
      options: {
        errorCatching: true,
        method: 'POST',
        loading: true,
        data: {
          codStep: params.idPreStep || undefined,
          idEvento: stateAct.idEvento,
        },
      },
      transformer: transformerActWizard,
    }
  );

  const onGetDataSaved = useEventCallback((isSetRecap: boolean) => {
    if (!stateAct.idEvento) {
      return;
    }

    setIsLoadingDraft(true);
    (
      getDataSaved({}) as unknown as ResponsePromise<
        HttpResponse<DataWizardAct>,
        HttpResponse<DataWizardAct>
      >
    ).then((response) => {
      setIsLoadingDraft(false);
      const castedResponseQuestionario: Array<QuestionarioEvento> =
        (response as AxiosResponse<HttpResponse<DataWizardAct>>).data.payload.questionarioEvento ||
        [];

      const castedResponseStatoCivile: StatoCivile =
        (response as AxiosResponse<HttpResponse<DataWizardAct>>).data.payload.statoCivile || {};

      const castedResponseEvento: Evento =
        (response as AxiosResponse<HttpResponse<DataWizardAct>>).data.payload.evento || {};

      if (!tipoEvento) {
        setTipoEvento(castedResponseEvento.tipoEvento || '');
      }

      const lastStepInitial = transformerActWizardDataSaved(
        response as AxiosResponse<HttpResponse<DataWizardAct>>
      );

      setLastStepInitialData(lastStepInitial);

      const recapInitial: Array<RecapVM> = castedResponseQuestionario.map((item) => {
        return {
          step: item.codStep || '',
          request: item.descBreveDomanda || '',
          response: item.descBreveRisposta || '',
          codResponse: item.idRisposta,
          disabled: true,
          codStepSuccessivo: item.cdStepSuccessivo || '',
          tipoAtto: castedResponseStatoCivile.tipoAtto || undefined,
          flagAusilioInterprete: castedResponseEvento.flagAusilioInterprete || '',
          parte: castedResponseStatoCivile.parteAtto || undefined,
          serie1: castedResponseStatoCivile.serie1Atto || undefined,
          serie2: castedResponseStatoCivile.serie2Atto || undefined,
          nascitaIstat: castedResponseStatoCivile.nascitaIstat || undefined,
          luogoEvento: castedResponseStatoCivile.luogoEvento || undefined,
          tipoResidenzaNascituro: castedResponseStatoCivile.tipoResidenzaNascituro || undefined,
          idAnsc: castedResponseStatoCivile.idAnsc || undefined,
          codiceStatoEvento: castedResponseStatoCivile.codiceStatoEvento || undefined,
          numeroAtto: castedResponseStatoCivile.numeroAtto || undefined,
          tipoContenuto: castedResponseEvento.tipoContenuto || undefined,
          role: castedResponseEvento.ruoloOperatore || undefined,
        } as RecapVM;
      });

      if ((recap || []).length === 0 || isSetRecap) {
        // solamente la prima volta quando è vuoto
        setRecap(recapInitial);
      }

      // chiamata per ultimo step
      (
        getRequests({
          data: {
            codStep:
              castedResponseQuestionario[
                castedResponseQuestionario.length - 1
              ].cdStepSuccessivo?.toString() || undefined,
            idEvento: stateAct.idEvento,
          },
        }) as ResponsePromise<HttpResponse<ActWizard>, RequestsType>
      ).then((responseRequests) => {
        const castedResponse = responseRequests as RequestsType;

        if (castedResponse.requests?.length > 0) {
          return;
        }

        setStateAct({
          idEvento: stateAct.idEvento,
          isLastStep: true,
          isLastStepEmergency:
            castedResponseEvento.codiceStatoEvento === STATES_ACT.ID_STATE_BOZZA_EMERGENZA,
        });
      });
    });
  });

  const saveData = useHttpClient(`${__ACT_API_URL__}/wizard/save`, {
    options: {
      method: 'POST',
      loading: true,
      errorCatching: true,
    },
  });

  // solo soggetti intestatari
  const [soggettiUseCase, getSoggettiUseCase] = useHttpFetch<
    HttpResponse<Array<SubjectsWizardUseCase>>,
    Array<SubjectsWizardUseCase>
  >(`${__ACT_API_URL__}/soggetti-use-case`, {
    onBootstrap: false,
    options: {
      errorCatching: true,
      method: 'POST',
      loading: true,
    },
    transformer: (response: AxiosResponse<HttpResponse<Array<SubjectsWizardUseCase>>>) => {
      const { data } = response;
      const filterData = ((data?.payload || []) as Array<SubjectsWizardUseCase>).filter(
        (currSoggetto) => currSoggetto.tipoSoggetto === 'intestatari'
      );

      return filterData;
    },
  });

  const soggettiUseCaseData: Array<SubjectsWizardUseCase> | undefined = useMemo(() => {
    return (soggettiUseCase?.data as Array<SubjectsWizardUseCase>)?.map(
      (item: SubjectsWizardUseCase) =>
        ({
          ...item,
          configurazione: item.configurazione?.trim(),
          codSoggetto: item.key?.codSoggetto?.trim(),
        } as SubjectsWizardUseCase)
    );
  }, [soggettiUseCase?.data]);

  useEffect(() => {
    const codLastStep =
      recap[recap.length - 1]?.codStepSuccessivo || recap[recap.length - 1]?.step || '';

    if (!Boolean(codLastStep)) {
      return;
    }

    if (!soggettiUseCase.isLoading && codLastStep !== UcCallSoggetti) {
      (
        getSoggettiUseCase({
          data: {
            codUseCase: codLastStep,
          },
        }) as unknown as ResponsePromise<Array<SubjectsWizardUseCase>>
      ).then(() => {
        setUcCallSoggetti(codLastStep);
      });
    }
  }, [stateAct, recap, UcCallSoggetti]);

  const createSchema = useEventCallback((requestsData: RequestsType) => {
    const validation: Record<string, unknown> = {};

    requestsData.requests?.forEach((item) => {
      if (item.type == IdType.Checkbox) {
        return;
      }
      validation[item.name] = yup.string().required('Il campo è obbligatorio');
    });

    return validation;
  });

  const validationSchema = useMemo(() => {
    if (stateAct.isLastStep) {
      const inputCreateSoggetti: Array<InputCreate> = (soggettiUseCaseData || []).map(
        (itemSubject) => ({
          configuration: itemSubject?.configurazione || '',
          presenza: itemSubject?.presenza || '',
          keySubject: `subject-${itemSubject?.codSoggetto}`,
          codeSubject: itemSubject?.codSoggetto || '',
          isDichiarante: itemSubject?.flagDichiarante === '1',
          isFirmatario: itemSubject?.flagFirmatario === '1',
        })
      );

      const dateFormationAct: DateTime = (requests?.data as RequestsType)?.dateFormationAct
        ? DateTime.fromISO((requests?.data as RequestsType)?.dateFormationAct || '')
        : DateTime.now();

      return createSchemaSubjectSectionStep(
        inputCreateSoggetti,
        dateFormationAct,
        false
      ) as yup.ObjectSchema<ObjectShape, Record<string, unknown>>;
    }

    if (!requests?.data) {
      return;
    }
    const validation: yup.ObjectSchema<ObjectShape, Record<string, unknown>> = yup.object(
      createSchema(requests?.data as RequestsType) as ObjectShape
    );

    return validation;
  }, [requests?.data, stateAct.isLastStep, soggettiUseCaseData]);

  const onSubmit = useEventCallback((values: StepVM) => {
    const name = (requests?.data as RequestsType)?.requests[0]?.name;
    // è impostata con una sola domanda
    (
      getRequests({
        data: {
          codStep: values[name]?.toString() || undefined,
          idEvento: stateAct.idEvento,
        },
      }) as ResponsePromise<HttpResponse<ActWizard>, RequestsType>
    ).then((responseRequests) => {
      const castedResponse = responseRequests as RequestsType;

      if (castedResponse.requests?.length > 0) {
        return;
      }

      setStateAct((oldState) => {
        if (!oldState.isLastStep) {
          return {
            idEvento: stateAct.idEvento,
            isLastStep: true,
          };
        }

        return oldState;
      });
    });
  });

  const goBack = useEventCallback(() => {
    navigate('/stato-civile/recupero-atti-emergenza');
  });

  const onSaveSubject = useEventCallback(
    (valuesSubject: ParentVM): ResponsePromise<number | undefined> | undefined => {
      // salvataggio dell'accordion del singolo soggetto (ci troviamo all'ultimo step)
      if (!valuesSubject?.generality) {
        return undefined;
      }

      const arrSubjects: Array<ParentVM> = [valuesSubject];

      const requestsData = requests?.data as RequestsType;

      const data = transformerActWizardToSave(
        true,
        recap,
        stateAct.idEvento,
        (requests?.data as RequestsType)?.dateFormationAct,
        arrSubjects,
        [],
        undefined,
        requestsData.idValore?.toString(),
        requestsData.descrizioneValore,
        stateAct.isLastStepEmergency
      );
      let idSubject = valuesSubject?.generality?.idSubject || undefined;

      return (
        saveData({
          data,
        }) as unknown as ResponsePromise<HttpResponse<DataWizardAct>, HttpResponse<DataWizardAct>>
      ).then((response) => {
        toast.success('Operazione eseguita con successo');

        const castedResponseStatoCivile: StatoCivile =
          (response as AxiosResponse<HttpResponse<DataWizardAct>>).data.payload.statoCivile || {};
        const castedResponseEvento: Evento =
          (response as AxiosResponse<HttpResponse<DataWizardAct>>).data.payload.evento || {};

        const recapNew = recap.map((item) => ({
          ...item,
          flagAusilioInterprete: castedResponseEvento.flagAusilioInterprete || undefined,
          nascitaIstat: castedResponseStatoCivile.nascitaIstat || undefined,
          luogoEvento: castedResponseStatoCivile.luogoEvento || undefined,
          tipoResidenzaNascituro: castedResponseStatoCivile.tipoResidenzaNascituro || undefined,
        }));

        setRecap(recapNew);

        const castedResponseSoggettoList: Array<SoggettoList> =
          (response as AxiosResponse<HttpResponse<DataWizardAct>>).data.payload.soggettoList || [];

        castedResponseSoggettoList.forEach((item) => {
          if (item?.codiceSoggetto === valuesSubject.codeSubject) {
            idSubject = item.key?.idSoggetto || undefined;
          }
        });

        return idSubject;
      }) as ResponsePromise<number | undefined>;
    }
  );

  const idUseCase: string = useMemo(() => {
    const requestsData = requests?.data as RequestsType;

    return requestsData?.idValore?.toString() || '';
  }, [requests?.data]);

  const title = useMemo(() => {
    const requestsData = requests?.data as RequestsType;
    if (idUseCase || requestsData?.descrizioneValore) {
      // last step

      const lastRecap = recap[recap.length - 1];

      return `${lastRecap?.parte || ''}${lastRecap?.serie1 || ''}${lastRecap?.serie2 || ''} ${
        idUseCase ? `[${idUseCase}]` : ''
      } ${requestsData?.descrizioneValore || ''}`.trim();
    }

    if (params.sottoMenuName?.toLowerCase() === 'nuovo') {
      return 'Stesura nuovo evento';
    }
    return 'Compilazione atto';
  }, [params.sottoMenuName, requests?.data, recap, idUseCase]);

  const onRedirectAfterValid = useEventCallback(() => {
    (
      onChangeState({
        url: `${__ACT_API_URL__}/bozza/update-stato/${stateAct.idEvento}/${STATES_ACT.ID_STATE_CONFERMATO}`,
      }) as ResponsePromise<void>
    ).then(() => {
      toast.success('Operazione eseguita con successo');

      navigate({
        pathname: generatePath(
          '/stato-civile/recupero-atti-emergenza/modifica/:idEvento/confermato',
          {
            idEvento: stateAct.idEvento?.toString() || '',
          }
        ),
      });
    });
  });

  const onClickValid = useEventCallback(() => {
    const url = `${__ACT_API_URL__}/evento/valida/${stateAct.idEvento}`;

    (
      onValid({
        url,
      }) as unknown as ResponsePromise<
        HttpResponse<ResponseValidateEvent>,
        HttpResponse<ResponseValidateEvent>
      >
    ).then((response) => {
      toast.success('Operazione eseguita con successo');
      const castedErrors: Array<ErrorsWarnings> =
        (response as AxiosResponse<HttpResponse<ResponseValidateEvent>>).data.payload.errors || [];

      const forcingCode: string =
        (response as AxiosResponse<HttpResponse<ResponseValidateEvent>>).data.payload.forcingCode ||
        '';

      if (castedErrors.length) {
        // se ci sono errori far scegliere se forzare la validazione
        setDeleteAlertModal({
          open: true,
          message: castedErrors.map((item) => item.text)?.join(', '),
          errors: castedErrors,
          forcingCode,
        });
        return;
      }

      onRedirectAfterValid();
    });
  });

  const onYesPreview = useEventCallback(() => {
    setModalPrintSuccess({ open: false });

    (
      onPreview() as unknown as ResponsePromise<
        HttpResponse<ResponsePreview>,
        HttpResponse<ResponsePreview>
      >
    ).then((response) => {
      toast.success('Operazione eseguita con successo');

      const responseCasted = response as AxiosResponse<HttpResponse<ResponsePreview>>;

      const castedErrors: Array<ErrorsWarnings> = responseCasted?.data.payload.errors || [];
      const titleError =
        castedErrors?.length > 1 ? `Ci sono ${castedErrors?.length} errori` : `C'è 1 errore`;
      const messagesError =
        (castedErrors?.map((item) => ({
          id: item.code,
          value: item.text,
        })) as Array<ItemVM>) || [];

      const castedWarnings: Array<ErrorsWarnings> = responseCasted?.data.payload.warnings || [];
      const titleWarning =
        castedWarnings?.length > 1 ? `Ci sono ${castedWarnings?.length} warning` : `C'è 1 warning`;
      const messagesWarning =
        (castedWarnings?.map((item) => ({
          id: item.code,
          value: item.text,
        })) as Array<ItemVM>) || [];

      setErrorWarningToVisualize({
        visibilityError: castedErrors?.length > 0,
        titleError,
        messagesError,
        visibilityWarning: castedWarnings?.length > 0,
        messagesWarning,
        titleWarning,
      });

      if (castedErrors?.length > 0) {
        return;
      }

      // se non ci sono errori effettuare stampa
      const pdf = responseCasted.data.payload.eventoOutput?.contenuto;
      if (pdf) {
        setPdfModal({
          data: pdf,
          name: 'Anteprima atto',
          open: true,
        });
      }
    });
  });

  const onPrintAct = useEventCallback(() => {
    setModalPrintSuccess({ open: false });

    (onPrint({ responseType: 'blob' }) as unknown as ResponsePromise<Blob>).then((response) => {
      toast.success('Operazione eseguita con successo');

      if (response?.data) {
        const blob = new Blob([response.data], { type: 'application/pdf' });
        blobToBase64(blob).then((base64) => {
          setPdfModal({
            data: base64.split(',')[1],
            name: 'Stampa atto',
            open: true,
          });
        });
      }
    });
  });

  const onConfirmRegistration = useEventCallback(() => {
    (
      onChangeState({
        url: `${__ACT_API_URL__}/bozza/update-stato/${stateAct.idEvento}/${STATES_ACT.ID_STATE_FIRMA_COMPLETA_USC_EMERGENZA}`,
      }) as ResponsePromise<void>
    ).then(() => {
      toast.success('Operazione eseguita con successo');

      navigate('/stato-civile');
    });
  });

  const onVisibilityError = useEventCallback(() => {
    setErrorWarningToVisualize({
      ...errorWarningToVisualize,
      visibilityError: false,
      titleError: '',
      messagesError: [],
    });
  });

  const onVisibilityWarning = useEventCallback(() => {
    setErrorWarningToVisualize({
      ...errorWarningToVisualize,
      visibilityWarning: false,
      titleWarning: '',
      messagesWarning: [],
    });
  });

  const backCompilation = useEventCallback(() => {
    (
      onChangeState({
        url: `${__ACT_API_URL__}/bozza/update-stato/${stateAct.idEvento}/${STATES_ACT.ID_STATE_BOZZA}`,
      }) as ResponsePromise<void>
    ).then(() => {
      // ricaricare i dati dell'ultimo step
      onGetDataSaved(true);

      setStateAct({
        idEvento: stateAct.idEvento,
        isLastStep: true,
      });
    });
  });

  const signatureUSC = useEventCallback(() => {
    // stato già aggiornato in ID_STATE_FIRMA_DICHIARANTE
    onGetDataSaved(false);
  });

  const verifyData = useHttpClient(
    `${__ACT_API_URL__}/firme/firma-dichiarante-elettronica/firmadichiarante/pooling-statoatto`,
    {
      options: {
        method: 'POST',
        loading: true,
        errorCatching: true,
      },
    }
  );

  const onClickVerify = useEventCallback(
    (values?: SignatureActVM): ResponsePromise<number> | void => {
      const data = {
        idSoggettoSide: (values?.signatories || []).map((item) => item.idSubject),
        idEventoSide: stateAct.idEvento?.toString() || undefined,
        firmaDichiaranteOnlinePoolingStatoAttoRequest: {
          idEvento:
            values?.idAnsc && values?.idAnsc.split('-') ? values?.idAnsc.split('-')[1] : undefined,
        },
      };

      return (
        verifyData({ data }) as unknown as ResponsePromise<HttpResponse<{ idState?: number }>>
      ).then((response) => {
        toast.success('Operazione eseguita con successo');

        return response?.data.payload.idState;
      }) as ResponsePromise<number>;
    }
  );

  const verifyDocument = useHttpClient(`${__ACT_API_URL__}/gestione-allegato-atti/verifica`, {
    options: {
      method: 'POST',
      loading: true,
      errorCatching: true,
    },
  });

  const onClickVerifyDocument = useEventCallback((document?: TypeFile): ResponsePromise<string> => {
    const data = {
      listaAllegatoAtti: [
        {
          key: {
            idAllegato: document?.idAllegato || undefined,
          },
        },
      ],
    };

    return (
      verifyDocument({
        data,
      }) as unknown as ResponsePromise<
        HttpResponse<{ listaAllegatoAtti?: Array<AllegatoAtti>; errori?: Array<string> }>,
        HttpResponse<{ listaAllegatoAtti?: Array<AllegatoAtti>; errori?: Array<string> }>
      >
    ).then((response) => {
      toast.success('Operazione eseguita con successo');

      const castedResponse: Array<AllegatoAtti> =
        (response as AxiosResponse<HttpResponse<{ listaAllegatoAtti?: Array<AllegatoAtti> }>>).data
          .payload.listaAllegatoAtti || [];

      return castedResponse[0]?.codStato || '';
    }) as ResponsePromise<string>;
  });

  const onForceValid = useHttpClient(`${__ACT_API_URL__}/evento/forza-validazione`, {
    options: {
      method: 'POST',
      loading: true,
      errorCatching: true,
    },
  });

  const onYesWithErrors = useEventCallback(() => {
    (
      onForceValid({
        data: {
          evento: {
            id: stateAct.idEvento,
          },
          errors: deleteAlertModal.errors || undefined,
          forcingCode: deleteAlertModal.forcingCode || undefined,
        },
      }) as unknown as ResponsePromise<unknown>
    ).then(() => {
      toast.success('Operazione eseguita con successo');

      setDeleteAlertModal({
        open: false,
      });

      onRedirectAfterValid();
    });
  });

  const initialValues: Record<string, string | boolean | ParentVM | DocumentVM | SectionVM> =
    useMemo(() => {
      const res: Record<string, string | boolean | ParentVM | DocumentVM | SectionVM> = {};

      if (stateAct.isLastStep) {
        // se ci troviamo nell'ultimo step
        soggettiUseCaseData?.forEach((itemSubject) => {
          const keySubject = `subject-${itemSubject?.codSoggetto}`;

          res[keySubject] = {
            ...initialValuesParent,
            eventType: tipoEvento,
            generality: {
              ...initialValuesParent?.generality,
              gender:
                itemSubject?.codSoggetto === CodeSubject.PADRE &&
                itemSubject.configurazione === ConfigurationType.BASESD
                  ? ID_GENDERS.MASCHIO
                  : itemSubject?.codSoggetto === CodeSubject.MADRE &&
                    itemSubject.configurazione === ConfigurationType.BASESD
                  ? ID_GENDERS.FEMMINA
                  : '',
            },
            configuration: itemSubject?.configurazione,
            codeSubject: itemSubject?.codSoggetto,
            flagComparente: itemSubject?.flagComparente || undefined,
            flagDichiarante: itemSubject?.flagDichiarante || undefined,
            tipoSoggetto: itemSubject?.tipoSoggetto || undefined,
            flagFirmatario: itemSubject?.flagFirmatario || undefined,
            presenza: itemSubject?.presenza || undefined,
            ...(lastStepInitialData ? (lastStepInitialData[keySubject] as ParentVM) : {}),
          };
        });

        return {
          ...(res || {}), // initial subjects and sections
          document: (lastStepInitialData?.document as DocumentVM) || undefined, // initial document
          documentEmergencyAct:
            (lastStepInitialData?.documentEmergencyAct as DocumentVM) || undefined, // initial documentEmergencyAct
          textAct: lastStepInitialData?.textAct || undefined, // initial text act
          textAnnotationOld: lastStepInitialData?.textAnnotationOld || undefined,
        };
      }

      // se ci troviamo nella navigazione

      if (requests?.data === undefined) {
        // error
        return res;
      }

      if (((requests?.data as RequestsType)?.requests || []).length === 0) {
        // nessuna risposta, mostrare ultimo step
        return res;
      }

      ((requests?.data as RequestsType)?.requests || []).forEach((item) => {
        res[item.name] = item.type === IdType.Checkbox ? false : '';
      });

      return res;
    }, [
      requests?.data,
      stateAct.isLastStep,
      soggettiUseCaseData,
      lastStepInitialData,
      stateAct.isSignatureUSC,
      stateAct.idEvento,
      tipoEvento,
      stateAct.allegatoList,
    ]);

  useEffect(() => {
    onGetDataSaved(true);
  }, [stateAct.idEvento]);

  const isLoading = soggettiUseCase.isLoading || isLoadingDraft;

  return (
    <Fragment>
      <Breadcrumbs items={ActEmergencyEditCrumbs} />
      {isLoading ? undefined : (
        <Formik
          initialValues={initialValues as StepVM}
          validationSchema={validationSchema}
          validateOnMount
          onSubmit={onSubmit}
          enableReinitialize
          isInitialValid
          validateOnChange
          validateOnBlur
        >
          <Form>
            <Grid container spacing={2}>
              <TitleStep
                isStepFromEmergency
                onGetDataSaved={onGetDataSaved}
                title={title}
                isLastStep
                idEvento={stateAct.idEvento}
                dateFormationAct={(requests?.data as RequestsType)?.dateFormationAct}
                lastRecap={recap.length > 0 ? recap[recap.length - 1] : undefined}
              />
              <StepForm
                isStepFromEmergency
                goBack={goBack}
                onSave={noop}
                onClickRecap={noop}
                onSaveSubject={onSaveSubject}
                onSaveSection={noop}
                onSaveText={noop}
                onClickValid={onClickValid}
                onClickEditAct={noop}
                onClickPreview={onYesPreview}
                deleteEvent={noop}
                onYesPreview={onYesPreview}
                onPrintAct={onPrintAct}
                onConfirmRegistration={onConfirmRegistration}
                backCompilation={backCompilation}
                onClickVerify={onClickVerify}
                onClickVerifyDocument={onClickVerifyDocument}
                signatureUSC={signatureUSC}
                transcribeOath={noop}
                soggettiUseCaseData={soggettiUseCaseData}
                onForwardCertificateConformity={noop}
                dateFormationAct={(requests?.data as RequestsType)?.dateFormationAct}
                recap={recap}
                setRecap={setRecap}
                requests={requests?.data as RequestsType}
                tipoEvento={tipoEvento}
                idUseCase={idUseCase}
                isLastStep // fixed
                onClickNotification={noop}
                onClickPrintCommunication={noop}
                onParent={noop}
                onOther={noop}
                onCohabitation={noop}
                onRegisterDeath={noop}
                onRegisterMarriageCivilUnion={noop}
                onRegisterCitizenship={noop}
                goBackNotification={noop}
                onCancelNotification={noop}
              />
              <Grid item xs={12}>
                <SearchMessages
                  type={MessageType.Error}
                  title={errorWarningToVisualize.titleError || ''}
                  messages={errorWarningToVisualize.messagesError || []}
                  visibility={errorWarningToVisualize.visibilityError || false}
                  onChangeVisibility={onVisibilityError}
                />
              </Grid>
              <Grid item xs={12}>
                <SearchMessages
                  type={MessageType.Warning}
                  title={errorWarningToVisualize.titleWarning || ''}
                  messages={errorWarningToVisualize.messagesWarning || []}
                  visibility={errorWarningToVisualize.visibilityWarning || false}
                  onChangeVisibility={onVisibilityWarning}
                />
              </Grid>
            </Grid>
          </Form>
        </Formik>
      )}
      {deleteAlertModal.open ? (
        <DeleteAlertModal
          title="Attenzione!"
          message={deleteAlertModal.message || ''}
          open={deleteAlertModal.open}
          onClose={() => setDeleteAlertModal({ open: false, step: '', index: 0, message: '' })}
          onYes={deleteAlertModal.errors ? onYesWithErrors : noop}
          onNo={() => setDeleteAlertModal({ open: false, step: '', index: 0, message: '' })}
          yesError
          labelYes={deleteAlertModal.errors ? 'Prosegui accettando le anomalie' : undefined}
          labelNo={deleteAlertModal.errors ? 'Torna alla compilazione' : undefined}
        />
      ) : undefined}
      {pdfModal.open ? (
        <ModalViewerPDF
          open={pdfModal.open}
          data={pdfModal.data}
          name={pdfModal.name}
          title="Anteprima atto"
          onClose={() => {
            setPdfModal({ data: '', open: false, name: '' });
            setModalPrintSuccess((oldState) => ({ ...oldState, open: true }));
          }}
        />
      ) : undefined}
      {modalPrintSuccess.open ? (
        <ModalPrintSuccess
          open={modalPrintSuccess.open}
          onYes={() => setModalPrintSuccess({ open: false })}
          onClose={() => (stateAct.isLastStepEmergency ? onPrintAct() : onYesPreview())}
        />
      ) : undefined}
    </Fragment>
  );
};

StepEmergencyPageComponent.displayName = 'Step Emergency Page';

const StepEmergencyPage = StepEmergencyPageComponent;

export default StepEmergencyPage;
